import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../App.css';
import './Dashboard.css';
import freshroadlogo from '../images/freshroadlogo.png';
import ScriptManager from './ScriptManager.js';
import AgentLogs from './Agentlogs.js';
import UploadKnowledgeBase from './UploadKnowledgeBase.js';
import ChatAgent from './Chatbot.js';
import CrmCombinedPage from './CrmCombined.js';
import CalendarPage from './CalendarPage.js';
import Workflow from './Workflow.js';
import ContactsPage from './ContactsPage.js';
import CreateScript from './CreateScript.js';
import PhonePage from './Phone.js';
import SMS from './SMS.js';
import BrowserVoice from './BrowserVoice.js';
import UserManagement from './UserManagement.js';
import Home from './Home.js';
import BatchCall from './BatchCall.js';
import Billing from './Billing.js';
import PhoneNumbers from './PhoneNumbers.js';
import AnalyticsSetup from './AnalyticsSetup.js';
import UserDashboard from './UserDashboard.js';
import SettingsPage from './Settings.js';
import Connections from './Connections.js';
import {
  LayoutDashboard,
  Bot,
  PenTool,
  Library,
  Mic,
  MessageSquare,
  Phone,
  PhoneIncoming,
  PhoneCall,
  Upload,
  BookOpen,
  BarChart3,
  Users,
  CreditCard,
  MessageCircle,
  Database,
  FileText,
  ChevronDown,
  ChevronRight,
  Activity,
  Settings,
  UserCircle,
  HelpCircle,
  Plug,
  Calendar
} from 'lucide-react';
import DashboardTutorialModal from '../components/DashboardTutorialModal.js';
import GeneralAnalytics from './GeneralAnalytics.js';
import AnalysisTestUI from './AnalysisTestUI.js';
import { EventEmitterType, useEventEmitterContext } from '../context/event-emitter.js';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasAdminAccess } = useAuth();
  const [selectedMenuItem, setSelectedMenuItem] = useState('Agents');
  const [pendingCreateScript, setPendingCreateScript] = useState(false);
  const [analyticsSetupSelection, setAnalyticsSetupSelection] = useState(null);
  const [generalAnalyticsSelection, setGeneralAnalyticsSelection] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    Train: true,
    Deploy: true,
    Insights: true,
    Account: true
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const { eventEmitter } = useEventEmitterContext();

  useEffect(() => {
    const search = location.search || '';
    if (search.includes('scripts=create')) {
      setSelectedMenuItem('Agents');
      setPendingCreateScript(true);
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!location.state?.openDashboardTutorial) return;
    setTutorialOpen(true);
    navigate(
      { pathname: location.pathname, search: location.search || '' },
      { replace: true, state: {} }
    );
  }, [location.state?.openDashboardTutorial, location.pathname, location.search, navigate]);

  eventEmitter?.useSubscription((event) => {
    if (event?.type !== EventEmitterType.DashboardSelectMenu) return;
    let menuItem = '';
    let params = null;

    if (typeof event?.payload === 'string') {
      menuItem = event.payload.trim();
    } else if (
      event?.payload &&
      typeof event.payload === 'object' &&
      !Array.isArray(event.payload)
    ) {
      menuItem = String(event.payload.menuItem || '').trim();
      params = event.payload.params && typeof event.payload.params === 'object'
        ? event.payload.params
        : null;
    }

    if (!menuItem) return;

    if (menuItem === 'Analytics Setup' && params) {
      setAnalyticsSetupSelection({
        scriptId: String(params.scriptId || '').trim(),
        scriptName: String(params.scriptName || '').trim(),
        tab: String(params.tab || '').trim(),
        highlightGenerate: !!params.highlightGenerate,
        nonce: Date.now(),
      });
    }

    if ((menuItem === 'General Analytics' || menuItem === 'Survey Analytics' || menuItem === 'Survey analytics') && params) {
      setGeneralAnalyticsSelection({
        scriptId: String(params.scriptId || '').trim(),
        scriptName: String(params.scriptName || '').trim(),
        highlightGenerate: !!params.highlightGenerate,
        nonce: Date.now(),
      });
    }

    if (menuItem === 'Survey Analytics' || menuItem === 'Survey analytics') {
      menuItem = 'General Analytics';
    }

    setSelectedMenuItem(menuItem);
    if (isMobile) {
      setSidebarOpen(false);
    }
  });

  // Mobile detection and sidebar state management
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false); // Auto-close sidebar when switching to desktop
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load sidebar collapsed state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState === 'true') {
      setSidebarCollapsed(true);
    }
  }, []);

  // Save sidebar collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  // Lock body scrolling when dashboard is mounted
  useEffect(() => {
    document.body.classList.add('dashboard-active');
    document.documentElement.classList.add('dashboard-active');
    return () => {
      document.body.classList.remove('dashboard-active');
      document.documentElement.classList.remove('dashboard-active');
    };
  }, []);

  // Build menu items grouped by section (matching new design)
  const menuStructure = [
    {
      section: 'Train',
      items: [
        { name: 'Agents', Icon: FileText },
        { name: 'Knowledge Base', Icon: Library },
      ]
    },
    {
      section: 'Deploy',
      items: [
        { name: 'Outbound', Icon: PhoneCall },
        { name: 'Inbound', Icon: PhoneIncoming },
        { name: 'SMS', Icon: MessageCircle },
        { name: 'Chatbot', Icon: MessageSquare },
      ]
    },
    {
      section: 'Insights',
      items: [
        { name: 'CRM', Icon: Database },
        { name: 'Calendar', Icon: Calendar },
        { name: 'Logs', Icon: BookOpen },
        { name: 'General Analytics', Icon: Activity },
        { name: 'Analytics Setup', Icon: Settings },
      ]
    },
    {
      section: 'Account',
      items: [
        { name: 'My Dashboard', Icon: UserCircle },
        { name: 'Connections', Icon: Plug },
        { name: 'Phone Numbers', Icon: Phone },
        { name: 'User Management', Icon: Users },
      ]
    },
  ];

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Add User Management for admins
  if (!hasAdminAccess()) {
    // Non-admins shouldn't see user management or billing
  }

  const renderContent = () => {
    switch (selectedMenuItem) {
      case 'Home':
        return <Home />;
      
      // Agent section
      case 'Inbound':
        return <PhonePage mode="inbound" />;
      case 'Outbound':
        return <PhonePage mode="outbound" />;
      case 'SMS':
        return <SMS />;
      case 'Chatbot':
        return <ChatAgent />;
      case 'Agents':
        return (
          <ScriptManager
            createNewScript={pendingCreateScript}
            onConsumeCreateNewScript={() => setPendingCreateScript(false)}
          />
        );
      case 'Knowledge Base':
        return <UploadKnowledgeBase />;
      
      // Insights section
      case 'CRM':
        return <CrmCombinedPage />;
      case 'Calendar':
        return <CalendarPage />;
      case 'Logs':
        return <AgentLogs />;
      case 'Analysis Test UI':
        return <AnalysisTestUI />;
      case 'Analytics Setup':
        return (
          <AnalyticsSetup
            initialSelection={analyticsSetupSelection}
            onSelectionApplied={() => setAnalyticsSetupSelection(null)}
          />
        );
      case 'General Analytics':
        return (
          <GeneralAnalytics
            initialSelection={generalAnalyticsSelection}
            onSelectionApplied={() => setGeneralAnalyticsSelection(null)}
          />
        );
      
      // Account section
      case 'My Dashboard':
        return <UserDashboard />;
      case 'Connections':
        return <Connections />;
      case 'Settings':
        return <SettingsPage />;
      case 'Phone Numbers':
        return <PhoneNumbers />;
      case 'User Management':
        return <UserManagement />;
      
      // Legacy cases for backward compatibility
      case 'Create Script':
        return <CreateScript />;
      case 'Outbound Voice Call':
        return <PhonePage mode="outbound" />;
      case 'Inbound Voice Call':
        return <PhonePage mode="inbound" />;
      case 'Voicebot Agent':
        return <BrowserVoice />;
      case 'Chatbot Agent':
        return <ChatAgent />;
      case 'Batch Call':
        return <BatchCall />;
      case 'Call Logs':
        return <AgentLogs />;
      case 'Survey analytics':
        return (
          <GeneralAnalytics
            initialSelection={generalAnalyticsSelection}
            onSelectionApplied={() => setGeneralAnalyticsSelection(null)}
          />
        );
      case 'Workflow':
      default:
        return (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">Select an option from the menu</p>
          </div>
        );
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigateTutorialMenu = (menuItem) => {
    if (!menuItem) return;
    if (menuItem === 'Analytics Setup') setAnalyticsSetupSelection(null);
    if (menuItem === 'General Analytics') setGeneralAnalyticsSelection(null);
    setSelectedMenuItem(menuItem);
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div className={`dashboard-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`} id="sidebar">
        <button 
          className="sidebar-toggle" 
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          aria-label="Toggle sidebar"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        
        <div className="logo">
          <img 
            src={freshroadlogo} 
            alt="Freshroad Health" 
            className="logo-img"
          />
        </div>

        <div className="shrink-0 px-6 pb-3">
          <button
            type="button"
            onClick={() => {
              setTutorialOpen(true);
              if (isMobile) setSidebarOpen(false);
            }}
            className="nav-item w-full"
            title="Dashboard tutorial"
            aria-label="Open dashboard tutorial"
          >
            <HelpCircle className="nav-icon" />
            {!sidebarCollapsed && <span>Tutorial</span>}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto min-h-0">
          {menuStructure.map((section) => {
            const isExpanded = expandedSections[section.section];
            return (
              <div key={section.section} className="nav-section">
                <button
                  onClick={() => toggleSection(section.section)}
                  className="nav-label"
                >
                  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {section.section === 'Train' && (
                      <>
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                      </>
                    )}
                    {section.section === 'Deploy' && (
                      <>
                        <rect x="3" y="11" width="18" height="10" rx="2" ry="2"/>
                        <circle cx="12" cy="5" r="2"/>
                        <path d="M12 7v4"/>
                        <line x1="8" y1="16" x2="8" y2="16"/>
                        <line x1="16" y1="16" x2="16" y2="16"/>
                        <path d="M9 21v-4"/>
                        <path d="M15 21v-4"/>
                      </>
                    )}
                    {section.section === 'Insights' && (
                      <>
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                        <line x1="12" y1="22.08" x2="12" y2="12"/>
                      </>
                    )}
                    {section.section === 'Account' && (
                      <>
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                      </>
                    )}
                  </svg>
                  <span>{section.section}</span>
                  {!sidebarCollapsed && (
                    <span className="nav-chevron">
                      {isExpanded ? <ChevronDown /> : <ChevronRight />}
                    </span>
                  )}
                </button>
                {isExpanded && section.items && section.items.map((menuItem) => (
                  <button
                    key={menuItem.name}
                    onClick={() => {
                    if (menuItem.name === 'Analytics Setup' && selectedMenuItem !== 'Analytics Setup') {
                        setAnalyticsSetupSelection(null);
                      }
                    if (menuItem.name === 'General Analytics' && selectedMenuItem !== 'General Analytics') {
                      setGeneralAnalyticsSelection(null);
                    }
                    setSelectedMenuItem(menuItem.name);
                      if (isMobile) {
                        setSidebarOpen(false);
                      }
                    }}
                    className={`nav-item ${selectedMenuItem === menuItem.name ? 'active' : ''}`}
                  >
                    <menuItem.Icon className="nav-icon" />
                    <span>{menuItem.name}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        {/* Settings — bottom of sidebar */}
        <div className="shrink-0 border-t border-gray-200/80 pt-3 pb-2 px-3 mt-auto flex flex-col gap-1">
          <button
            type="button"
            onClick={() => {
              setSelectedMenuItem('Settings');
              if (isMobile) setSidebarOpen(false);
            }}
            className={`nav-item w-full ${selectedMenuItem === 'Settings' ? 'active' : ''}`}
            title="Settings"
          >
            <Settings className="nav-icon" />
            {!sidebarCollapsed && <span>Settings</span>}
          </button>
        </div>
      </aside>

      {/* Mobile hamburger - visible when sidebar is collapsed so user can open it */}
      {isMobile && sidebarCollapsed && (
        <button
          type="button"
          onClick={() => setSidebarCollapsed(false)}
          className="fixed top-4 left-4 z-50 flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 shadow-md border border-gray-200 text-gray-700 hover:bg-gray-50"
          aria-label="Open menu"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      )}

      {/* Main Content - top bar + inner scroll region */}
      <main className="main-content">
        <div
          className={`main-content-inner ${['Inbound', 'Outbound', 'SMS', 'Agents', 'Knowledge Base', 'Logs', 'Inbound Voice Call', 'Outbound Voice Call', 'Call Logs', 'CRM'].includes(selectedMenuItem) ? 'main-content-inner--full-bleed' : ''} ${selectedMenuItem === 'CRM' ? 'main-content-inner--crm-v2' : ''}`}
        >
          <div className="main-content-page">
            {renderContent()}
          </div>
        </div>
      </main>

      <DashboardTutorialModal
        open={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
        onNavigate={navigateTutorialMenu}
      />
    </div>
  );
};

export default Dashboard;
